import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { Menu, User, X, ChevronDown, LogOut, LayoutDashboard } from 'lucide-react';
import { CartIcon } from './CartIcon';

const DESKTOP_LINKS = [
  { label: 'Kopen', to: '/cadeaubonnen-kopen' },
  { label: 'Verkopen', to: '/cadeaubonnen-verkopen' },
  { label: 'Hoe werkt het', to: '/how-it-works' },
];

const Navbar = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const accountMenuRef = useRef(null);
  const mobilePanelRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setAccountMenuOpen(false);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeAllMenus = () => {
    setAccountMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const handleNavigate = (to) => {
    closeAllMenus();
    navigate(to);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      closeAllMenus();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Fout bij uitloggen. Probeer het opnieuw.');
    }
  };

  const accountMenuId = 'navbar-account-menu';
  const mobileMenuId = 'navbar-mobile-menu';

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-orange-100 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center">
            <div className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-2xl font-semibold text-transparent">
              Swapify
            </div>
          </Link>

          <div className="hidden md:flex items-center justify-center gap-8">
            {DESKTOP_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-sm font-medium text-gray-700 transition hover:text-red-500"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <CartIcon />

            <div ref={accountMenuRef} className="relative hidden md:block">
              {loading ? (
                <div className="h-11 w-11 animate-pulse rounded-full bg-orange-100" />
              ) : (
                <>
                  <button
                    type="button"
                    aria-expanded={accountMenuOpen}
                    aria-controls={accountMenuId}
                    onClick={() => setAccountMenuOpen((open) => !open)}
                    className="flex h-11 items-center gap-2 rounded-full border border-orange-200 bg-white px-3 text-gray-700 shadow-sm transition hover:border-orange-300 hover:bg-orange-50"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                      <User className="h-4 w-4" />
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-500 transition ${accountMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {accountMenuOpen && (
                    <div
                      id={accountMenuId}
                      className="absolute right-0 mt-3 w-64 rounded-xl border border-orange-100 bg-white p-2 shadow-[0_24px_60px_-28px_rgba(251,146,60,0.35)]"
                    >
                      {user ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleNavigate('/dashboard')}
                            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-orange-50 hover:text-orange-700"
                          >
                            <LayoutDashboard className="h-4 w-4" />
                            Mijn Account
                          </button>
                          <button
                            type="button"
                            onClick={() => handleNavigate('/dashboard')}
                            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-orange-50 hover:text-orange-700"
                          >
                            <LayoutDashboard className="h-4 w-4" />
                            Mijn Advertenties
                          </button>
                          <div className="my-2 border-t border-orange-100" />
                          <button
                            type="button"
                            onClick={handleLogout}
                            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-orange-50 hover:text-orange-700"
                          >
                            <LogOut className="h-4 w-4" />
                            Uitloggen
                          </button>
                        </>
                      ) : (
                        <>
                          <Link
                            to="/login"
                            onClick={closeAllMenus}
                            className="block rounded-lg px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-orange-50 hover:text-orange-700"
                          >
                            Inloggen
                          </Link>
                          <Link
                            to="/login"
                            onClick={closeAllMenus}
                            className="mt-2 block rounded-xl bg-gradient-to-r from-red-500 via-orange-500 to-red-500 px-4 py-3 text-center text-sm font-semibold text-white shadow-[0_16px_40px_-24px_rgba(239,68,68,0.5)] transition hover:-translate-y-0.5 hover:shadow-lg"
                          >
                            Aanmelden
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              aria-expanded={mobileMenuOpen}
              aria-controls={mobileMenuId}
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-orange-200 bg-white text-gray-700 shadow-sm transition hover:border-orange-300 hover:bg-orange-50 md:hidden"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            aria-label="Sluit menu"
            className="absolute inset-0 bg-black/35"
            onClick={closeAllMenus}
          />
          <div
            id={mobileMenuId}
            ref={mobilePanelRef}
            className="absolute right-0 top-0 h-full w-full max-w-sm border-l border-orange-100 bg-white shadow-[0_32px_80px_-32px_rgba(251,146,60,0.35)]"
          >
            <div className="flex items-center justify-between border-b border-orange-100 px-5 py-4">
              <div className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-xl font-semibold text-transparent">
                Menu
              </div>
              <button
                type="button"
                onClick={closeAllMenus}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-orange-200 bg-white text-gray-700 transition hover:bg-orange-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex h-[calc(100%-73px)] flex-col overflow-y-auto px-5 py-5">
              <div className="space-y-2">
                <Link
                  to="/cadeaubonnen-kopen"
                  onClick={closeAllMenus}
                  className="block rounded-xl px-4 py-3 text-base font-medium text-gray-800 transition hover:bg-orange-50 hover:text-orange-700"
                >
                  Kopen
                </Link>
                <Link
                  to="/cadeaubonnen-verkopen"
                  onClick={closeAllMenus}
                  className="block rounded-xl px-4 py-3 text-base font-medium text-gray-800 transition hover:bg-orange-50 hover:text-orange-700"
                >
                  Verkopen
                </Link>
                <Link
                  to="/how-it-works"
                  onClick={closeAllMenus}
                  className="block rounded-xl px-4 py-3 text-base font-medium text-gray-800 transition hover:bg-orange-50 hover:text-orange-700"
                >
                  Hoe werkt het
                </Link>
              </div>

              <div className="my-5 border-t border-orange-100" />

              <div className="space-y-2">
                {loading ? (
                  <div className="h-12 animate-pulse rounded-xl bg-orange-100" />
                ) : user ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={closeAllMenus}
                      className="block rounded-xl px-4 py-3 text-base font-medium text-gray-800 transition hover:bg-orange-50 hover:text-orange-700"
                    >
                      Mijn Account
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="block w-full rounded-xl px-4 py-3 text-left text-base font-medium text-gray-800 transition hover:bg-orange-50 hover:text-orange-700"
                    >
                      Uitloggen
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={closeAllMenus}
                      className="block rounded-xl px-4 py-3 text-base font-medium text-gray-800 transition hover:bg-orange-50 hover:text-orange-700"
                    >
                      Inloggen
                    </Link>
                    <Link
                      to="/login"
                      onClick={closeAllMenus}
                      className="block rounded-xl bg-gradient-to-r from-red-500 via-orange-500 to-red-500 px-4 py-3 text-center text-base font-semibold text-white shadow-[0_16px_40px_-24px_rgba(239,68,68,0.5)] transition hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      Aanmelden
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
